import { PropsWithChildren } from 'react';

import { Box, chakra, Flex, Heading, Skeleton, Text, useRadio, useRadioGroup, UseRadioProps } from '@chakra-ui/react';

import { BillingFrequency } from '~/features/checkout/models/checkout';

export function PricePlanRadio({ children, ...props }: PropsWithChildren<UseRadioProps>) {
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } = useRadio(props);

  return (
    <chakra.label {...htmlProps} cursor='pointer' flex='1'>
      <input {...getInputProps({})} aria-label={props.value} />
      <Box
        {...getRadioProps()}
        bg={state.isChecked ? 'blue.100' : 'gray.100'}
        borderRadius={state.isChecked ? '2px' : undefined}
        p={1}
        w={12}
        width='full'
        {...(state.isFocused && {
          outline: '1px solid',
          outlineColor: 'blue.500',
          outlineOffset: '2px',
        })}
        padding={3}
        position='relative'
        sx={{
          '&::before': {
            content: '""',
            bgGradient: state.isChecked ? 'linear(to right, #3F7DFF, #B17CEF)' : 'linear(to right, gray.200, gray.200)',
            borderRadius: '4px',
            borderWidth: '2px',
            inset: 0,
            position: 'absolute',
            zIndex: -1,
            margin: '-2px',
          },
        }}
      >
        <Box {...getLabelProps()} color={state.isChecked ? 'black' : 'gray.600'}>
          {children}
        </Box>
      </Box>
    </chakra.label>
  );
}

export type PricePlanRadioGroupProps = {
  items: Array<{
    label: string;
    displayPrice?: string;
    displayPriceUnits?: string;
    value: BillingFrequency;
  }>;
  onChange: (value: BillingFrequency) => void;
  value: BillingFrequency;
};

export function PricePlanRadioGroup({ items, onChange, value }: PricePlanRadioGroupProps) {
  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: 'all',
    onChange,
    value,
  });
  return (
    <Flex {...getRootProps()} flexWrap='wrap' gap={3}>
      {items.map((item) => {
        return (
          <PricePlanRadio key={item.value} {...getRadioProps({ value: item.value })}>
            <Heading as={Text} fontSize='sm' fontWeight='400' textTransform='capitalize' whiteSpace='nowrap'>
              {item.label}
            </Heading>
            <Heading as={Text} color='black' size='marketing-md' whiteSpace='nowrap'>
              {item.displayPrice ? (
                <Box as='span' fontWeight='500 !important'>
                  {item.displayPrice}
                </Box>
              ) : (
                <Skeleton as='span' display='inline-block' height='14.5px' marginEnd={1} width='36px' />
              )}
              {item.displayPriceUnits ? (
                <Box as='span' fontWeight='400 !important'>
                  {item.displayPriceUnits}
                </Box>
              ) : (
                <Skeleton as='span' display='inline-block' height='14.5px' width='55px' />
              )}
            </Heading>
          </PricePlanRadio>
        );
      })}
    </Flex>
  );
}
